import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  focus,
  fontSans,
  ink,
  radius,
  space,
  typeRole,
  typeScale,
  weight,
} from '@repo/design-tokens';
import { Banner, Button, Card, FilterChipGroup, Icon } from '@repo/design-system';
import {
  BrandMixCard,
  IdentifiedScoreCard,
  TagSwitcher,
  visuallyHidden,
  type BrandMixCardState,
} from '@repo/ui';
import type { TwinBrandMix } from '@repo/front-end-services';
import type { ManufacturerIdentifiedScore } from '@repo/front-end-utils';

import type { EquipmentChip, EquipmentChipGroups } from './equipmentFilter';
import { clockTime } from './DraftStatusTag';

/**
 * **The Equipment step's frame** (`equipment-page-stage`, spec R1–R2, R5–R8, R11), from frame 04 of
 * Rob's 12 October *Equipment Page* design: a left panel (the *Manufacturer identified* score, *Your
 * brands*, the counted chips in *To do / Done / Plan*, the register file) and a main panel (the
 * view's title and count, then the step's Rows view (`EquipmentRowsView`) and its uploader as `children`).
 *
 * It owns layout and the panel's presentation only. The page owns every read, write and dialog, and
 * decides the rows (the one identify model, `equipmentFilter.ts`), so nothing here can count a
 * second way.
 *
 * ## One tree in every state
 *
 * The main panel is always the same `Card` in the same position, and `children` always sit in it;
 * only the left panel and the main panel's head come and go. Swapping the wrapper between states
 * would remount the uploader, whose own state (the chosen file, the upload's *Upload failed.* banner, the
 * match review) is exactly what an operator acts on straight after the first upload into an empty register
 * — the moment the step changes state (review, `equipment-page-stage`).
 *
 * ## The left panel (`panel`, spec R2 and Failure States) — decided by the page
 *
 * - `none`: no twin, or a register read as empty — no left panel (ruling 4). On an empty register
 *   the main panel holds the start screen instead (`equipment-page-start`; the page passes `rows`
 *   as `null` then, never `undefined`, so `children` keep their place).
 * - `loading`: the FIRST read of this twin's register — the cards in their loading look, no numbers.
 *   A re-read after a write is not this: the page keeps `ready` while the rows it already read are
 *   on screen, so the panel, the search and the tag do not flicker away after every save.
 * - `error`: the read failed — one sentence that the numbers are not shown, and no number anywhere.
 * - `uncounted`: the rows were read but the identify model could not be built from them.
 * - `ready`: everything.
 *
 * ## Rows / Groups (spec R11)
 *
 * The switch renders only when the page supplies `groupsView` — the Groups view is
 * `equipment-page-landed`'s — so it never offers an empty view (ruling 1).
 */
export type EquipmentPanel = 'none' | 'loading' | 'error' | 'uncounted' | 'ready';

export interface EquipmentFileCard {
  /**
   * The file's name: from the server's record of the twin's latest upload once that has been read
   * (`equipment-page-landed`, R5), else from this session's own upload while the read has not
   * answered; null when neither names one (ruling 2).
   */
  fileName: string | null;
  uploadedAt: Date | null;
  /** The upload's rows (created + updated) when known, else the register's total. */
  rows: number;
  /** Whether *Upload Updated Register* has the uploader open. */
  open: boolean;
  onToggle: () => void;
  /** The uploader body's id, for `aria-controls` while it is open. */
  controlsId: string;
  /** The button, so the page can return focus to it when an upload closes the controls. */
  toggleRef?: React.Ref<HTMLButtonElement>;
  /**
   * *Undo This Import* (`equipment-page-undo-import`, R6), after *Upload Updated Register*; absent
   * when no upload can be undone.
   */
  undoAction?: React.ReactNode;
  /** Part of the upload is undone: the meta line says so (R7). */
  partlyUndone?: boolean;
}

export interface EquipmentStageProps {
  panel: EquipmentPanel;
  score: ManufacturerIdentifiedScore | null;
  brandMix: { mix: TwinBrandMix | null; state: BrandMixCardState; retry: () => void };
  chips: EquipmentChipGroups;
  onToggleChip: (key: EquipmentChip) => void;
  file: EquipmentFileCard;
  /** *All assets*, or the active chip's label. */
  title: string;
  /** *214 assets*, or *1 of 2 assets match "tread"*. */
  countText: string;
  /**
   * What the polite region says in place of *{title}, {countText}* — *Needs a look: 12 assets.*
   * (`equipment-page-adoption`, R11). Absent for every chip.
   */
  announcement?: string | null;
  /**
   * Actions in the panel bar, right of the title (`equipment-page-confirm`, R7; frame 05:
   * *Confirm All 22*). Absent or null, the bar holds the title and the view switch only.
   */
  barActions?: React.ReactNode;
  /** The Groups view (`equipment-page-landed`); absent means no *Rows / Groups* switch. */
  groupsView?: React.ReactNode;
  /**
   * Which view shows, when the host decides it (`equipment-page-landed`, R8: the step lands on
   * Groups while anything is to do). Omitted, the stage keeps its own choice, starting on Rows.
   */
  view?: 'rows' | 'groups';
  onViewChange?: (view: 'rows' | 'groups') => void;
  /** What landed (`equipment-page-landed`, R2), above the content in both views. */
  summary?: React.ReactNode;
  /**
   * The register search, first in the left panel while it is ready, as on Floor Plan and Observers
   * (`onboarding-assets-search-in-panel`, R1). The page owns its query and passes it only then.
   */
  search?: React.ReactNode;
  /**
   * The Rows view (`EquipmentRowsView`, `equipment-page-table`) — shown only while Rows is chosen, and hidden, never
   * unmounted, while Groups shows, so it keeps its state. `children` (the uploader, its outcome
   * band and the step's notices) render above whichever view is chosen: *Upload Updated Register*
   * and a partial upload's refused-row notices must work in Groups too (Copilot review, PR #2077).
   * Omitted, `children` are the Rows view, as before.
   */
  rows?: React.ReactNode;
  /**
   * A docked card beside the main panel — the identify card (`equipment-page-identify`, R2; frame
   * 06: *The docked card sits beside the list, not over it*). It takes its own width; absent, the
   * main panel has the whole row, so the frame reserves nothing while no card is open.
   */
  dock?: React.ReactNode;
  /**
   * The bar for a selection (`equipment-page-table`, R13), rendered last in the main panel in both
   * views, where it sticks to the panel's visible foot.
   */
  selectionBar?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/** How long the count line waits after the last change before it is announced (typing). */
export const COUNT_ANNOUNCE_DELAY_MS = 300;

/** What the panel says when the numbers are withheld. */
export const EQUIPMENT_PANEL_ERROR =
  'The register could not be read, so these numbers are not shown.';
export const EQUIPMENT_PANEL_UNCOUNTED =
  'The register could not be counted, so these numbers are not shown.';

/*
 * Plain `styled.div`s only, with the design system's `Card` rendered INSIDE them at render time,
 * never `styled(Card)` at module scope: the wizard's page suites mock `@repo/design-system` with
 * just the parts each one renders, and a module-scope `styled(undefined)` would fail every one of
 * them on import, including the many that never open this step.
 */
const Root = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  gap: ${space[3]};
  padding: ${space[3]};
  box-sizing: border-box;

  > .equipment-side,
  > .equipment-main {
    display: flex;
    flex-direction: column;
    min-height: 0;
    gap: ${space[3]};
    overflow-y: auto;
  }

  /* The docked card keeps its own width (448 px, DockedCard's beside); the list narrows. */
  > .tl-docked {
    flex: none;
  }

  /* Frame 04 and the page file (left 76px, width 300px): the left panel is 300 px. */
  > .equipment-side {
    flex: 0 0 300px;
    width: 300px;
  }

  > .equipment-main {
    flex: 1;
    min-width: 0;
  }

  .equipment-file {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
  }
`;

const Chips = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
`;

/** *Upload Updated Register*, then *Undo This Import* beside it, wrapping when the card is narrow. */
const FileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]};
`;

const FileHead = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  column-gap: ${space[2]};
  color: ${ink[3]};

  .file-name {
    font-family: ${fontSans};
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    font-weight: ${weight.semibold};
    color: ${ink[1]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-meta {
    font-family: ${fontSans};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
    color: ${ink[3]};
  }
`;

const MainHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space[3]};

  h4 {
    margin: 0;
    font-family: ${fontSans};
    font-size: ${typeRole.body2.fontSize};
    line-height: ${typeRole.body2.lineHeight};
    font-weight: ${weight.semibold};
    color: ${ink[1]};
    border-radius: ${radius.sm};
  }
  /* Focused by script only (A2): the token ring when a keyboard brought it here, nothing on a click. */
  h4:focus {
    outline: none;
  }
  h4:focus-visible {
    box-shadow: ${focus.tight};
  }

  p {
    margin: 0;
    font-family: ${fontSans};
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
    color: ${ink[3]};
  }
`;

/** The bar's actions sit after the title, before the view switch (frame 05). */
const BarActions = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${space[2]};
  margin-left: auto;
`;

const rowsText = (n: number): string => `${n} ${n === 1 ? 'row' : 'rows'}`;

/**
 * *Uploaded today 14:32*, or *Uploaded 12 Oct 14:32* for an earlier day — the file card now names
 * uploads from the server's record, which can be from any day (Copilot review, PR #2077). Local
 * calendar dates, compared as such.
 */
export const uploadedWhen = (at: Date, now: Date = new Date()): string => {
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  const day = sameDay
    ? 'today'
    : at.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
      });
  return `Uploaded ${day} ${clockTime(at)}`;
};

const fileMeta = (file: EquipmentFileCard): string =>
  file.partlyUndone
    ? 'Part of this file is undone'
    : file.fileName && file.uploadedAt
      ? `${uploadedWhen(file.uploadedAt)} · ${rowsText(file.rows)}`
      : rowsText(file.rows);

/** The count line, said after the operator pauses rather than on every keystroke. */
const useSettledText = (text: string): string => {
  const [settled, setSettled] = useState(text);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(text), COUNT_ANNOUNCE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [text]);
  return settled;
};

export const EquipmentStage: React.FC<EquipmentStageProps> = ({
  panel,
  score,
  brandMix,
  chips,
  onToggleChip,
  file,
  title,
  countText,
  announcement,
  barActions,
  groupsView,
  view: hostView,
  onViewChange,
  summary,
  search,
  rows,
  dock,
  selectionBar,
  children,
  className,
}) => {
  const [ownView, setOwnView] = useState<'rows' | 'groups'>('rows');
  const view = hostView ?? ownView;
  const setView = (next: 'rows' | 'groups') => {
    if (hostView === undefined) setOwnView(next);
    onViewChange?.(next);
  };
  const ready = panel === 'ready';
  const announced = useSettledText(ready ? (announcement ?? `${title}, ${countText}`) : '');
  const showGroups = ready && groupsView !== undefined && view === 'groups';

  return (
    <Root className={className} data-testid="equipment-stage" data-panel={panel}>
      {panel !== 'none' && (
        <Card
          surface="on-scene"
          size="sm"
          className="equipment-side"
          role="complementary"
          aria-label="Equipment summary"
        >
          {panel === 'error' || panel === 'uncounted' ? (
            <Banner surface="on-glass" tone="error">
              {panel === 'error' ? EQUIPMENT_PANEL_ERROR : EQUIPMENT_PANEL_UNCOUNTED}
            </Banner>
          ) : panel === 'loading' ? (
            <>
              <Card surface="on-glass" size="sm" loading aria-busy="true">
                {null}
              </Card>
              <BrandMixCard
                mix={null}
                state="loading"
                onRetry={brandMix.retry}
                surface="on-glass"
              />
              <Card
                surface="on-glass"
                size="sm"
                loading
                aria-busy="true"
                className="equipment-file"
              >
                {null}
              </Card>
            </>
          ) : (
            <>
              {search != null && <div className="equipment-search">{search}</div>}
              {score && <IdentifiedScoreCard score={score} surface="on-glass" />}
              <BrandMixCard
                mix={brandMix.mix}
                state={brandMix.state}
                onRetry={brandMix.retry}
                surface="on-glass"
              />
              <Chips>
                <FilterChipGroup<EquipmentChip>
                  label="To do"
                  caption="above"
                  size="default"
                  chips={chips.todo}
                  onToggle={onToggleChip}
                />
                <FilterChipGroup<EquipmentChip>
                  label="Done"
                  caption="above"
                  size="default"
                  chips={chips.done}
                  onToggle={onToggleChip}
                />
                <FilterChipGroup<EquipmentChip>
                  label="Plan"
                  caption="above"
                  size="default"
                  chips={chips.plan}
                  onToggle={onToggleChip}
                />
              </Chips>
              <Card surface="on-glass" size="sm" className="equipment-file">
                <FileHead>
                  <Icon name="file-csv" variant="solid" />
                  <div>
                    <div className="file-name">{file.fileName ?? 'Register'}</div>
                    <div className="file-meta">{fileMeta(file)}</div>
                  </div>
                </FileHead>
                <FileActions>
                  <Button
                    ref={file.toggleRef}
                    type="button"
                    variant="frosted"
                    size="sm"
                    iconLeft={<Icon name="upload" size="sm" />}
                    aria-expanded={file.open}
                    aria-controls={file.open ? file.controlsId : undefined}
                    onClick={file.onToggle}
                  >
                    Upload Updated Register
                  </Button>
                  {file.undoAction}
                </FileActions>
              </Card>
            </>
          )}
        </Card>
      )}
      <Card
        surface="on-scene"
        size="md"
        className="equipment-main"
        role="region"
        aria-label={ready ? undefined : 'Equipment register'}
        aria-labelledby={ready ? 'equipment-main-title' : undefined}
      >
        {ready && (
          <MainHead>
            <div>
              {/* Focusable by script only: where focus goes when the last group a press acted on
                  leaves the list (`equipment-page-landed`, A2). Not a Tab stop. */}
              <h4 id="equipment-main-title" tabIndex={-1}>
                {title}
              </h4>
              <p>{countText}</p>
            </div>
            {barActions != null && <BarActions>{barActions}</BarActions>}
            {groupsView !== undefined && (
              <TagSwitcher
                label="View"
                options={[
                  { value: 'rows', text: 'Rows', icon: 'table-list' },
                  { value: 'groups', text: 'Groups', icon: 'layer-group' },
                ]}
                selectedValue={view}
                onChange={(value: string) => setView(value as 'rows' | 'groups')}
              />
            )}
          </MainHead>
        )}
        <span role="status" aria-live="polite" style={visuallyHidden}>
          {announced}
        </span>
        {ready && summary}
        {rows !== undefined && children}
        {/* Kept mounted while Rows shows, so which groups are open survives a look at the rows
            (Copilot review, PR #2077); hidden, as the list is while Groups shows. */}
        {ready && groupsView !== undefined && (
          <div hidden={!showGroups} style={{ display: showGroups ? 'contents' : 'none' }}>
            {groupsView}
          </div>
        )}
        {/* Hidden, never unmounted, while Groups shows: the list (and, with no `rows`, everything
            else) keeps its state. */}
        <div hidden={showGroups} style={{ display: showGroups ? 'none' : 'contents' }}>
          {rows !== undefined ? rows : children}
        </div>
        {ready && selectionBar}
      </Card>
      {dock}
    </Root>
  );
};

export default EquipmentStage;
